import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useRequests } from '../../../../requests/useRequests'
import { RefreshActions } from '../../../../state/refresh'
import { ProjectAttachment } from '../../../../types/project'

interface AttachmentsSectionProps {
  projectId: string
  isReadOnly?: boolean
  isList: boolean
}

export const AttachmentsSection = ({ projectId, isReadOnly = false, isList }: AttachmentsSectionProps) => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Stany dla załączników i folderów
  const [projectAttachments, setProjectAttachments] = useState<ProjectAttachment[]>([])
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [folders, setFolders] = useState<any[]>([])
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string | null, name: string }>>([
    { id: null, name: 'Główny katalog' }
  ])
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolderForm, setShowNewFolderForm] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Funkcja pobierająca foldery
  const fetchFolders = useCallback(async () => {
    try {
      const response = await requestMaker.getProjectAttachmentFolders(projectId, isList)
      // Filtrujemy foldery tylko dla aktualnego poziomu
      const filteredFolders = response.data.filter((folder: { parentId: number | null }) => {
        if (currentFolderId === null) {
          return folder.parentId === null
        }
        // Konwertujemy oba identyfikatory na liczby dla pewności
        return Number(folder.parentId) === Number(currentFolderId)
      })
      setFolders(filteredFolders)
    } catch (error) {
      console.error('Błąd podczas pobierania folderów', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać folderów'
        })
      )
    }
  }, [projectId, currentFolderId, dispatch])

  // Funkcja pobierająca załączniki
  const fetchProjectAttachments = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await requestMaker.getProjectAttachments(projectId, currentFolderId, isList)
      // Filtrujemy załączniki tylko dla aktualnego folderu
      const filteredAttachments = response.data.filter((attachment: { folderId: string | null }) => {
        if (currentFolderId === null) {
          return attachment.folderId === null
        }
        // Konwertujemy oba identyfikatory na stringi dla pewności
        return String(attachment.folderId) === String(currentFolderId)
      })
      setProjectAttachments(filteredAttachments)
    } catch (error) {
      console.error('Błąd podczas pobierania załączników', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać załączników'
        })
      )
    } finally {
      setIsLoading(false)
    }
  }, [projectId, currentFolderId, dispatch])

  // Funkcja do wyzwalania inputa plików
  const handleAttachmentClick = () => {
    if (fileInputRef.current != null) {
      fileInputRef.current.click()
    }
  }

  // Funkcja do obsługi przesyłania pliku
  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files == null) || e.target.files.length === 0) return

    const file = e.target.files[0]
    setIsUploadingAttachment(true)

    try {
      // Przesyłanie pliku do Backblaze
      const uploadResponse = await requestMaker.uploadFile(file, 'project')

      // Dodanie załącznika do projektu z uwzględnieniem folderu
      const attachment = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: uploadResponse.data.url,
        fileKey: uploadResponse.data.fileKey,
        folderId: currentFolderId,
        isList
      }

      await requestMaker.addProjectAttachment(projectId, attachment)

      await fetchProjectAttachments()
      await fetchFolders()

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Załącznik został dodany'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania załącznika', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać załącznika'
        })
      )
    } finally {
      setIsUploadingAttachment(false)
      // Zresetuj input pliku
      if (fileInputRef.current != null) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Funkcja do usuwania załącznika
  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten załącznik?')) return

    try {
      await requestMaker.deleteProjectAttachment(projectId, attachmentId)
      await fetchProjectAttachments()

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Załącznik został usunięty'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania załącznika', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć załącznika'
        })
      )
    }
  }

  // Funkcja do tworzenia nowego folderu
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return
    try {
      await requestMaker.createProjectAttachmentFolder(projectId, { name: newFolderName, parentId: currentFolderId, isList })
      await fetchFolders()
      setNewFolderName('')
      setShowNewFolderForm(false)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Folder został utworzony'
        })
      )
    } catch (error) {
      console.error('Błąd podczas tworzenia folderu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się utworzyć folderu'
        })
      )
    }
  }

  // Funkcja do usuwania folderu
  const handleDeleteFolder = async (folderId: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten folder?')) return

    try {
      await requestMaker.deleteProjectAttachmentFolder(projectId, folderId)
      if (currentFolderId === folderId) {
        if (breadcrumbs.length > 1) {
          const parentFolderIndex = breadcrumbs.length - 2
          setCurrentFolderId(breadcrumbs[parentFolderIndex].id)
          setBreadcrumbs(breadcrumbs.slice(0, parentFolderIndex + 1))
        } else {
          setCurrentFolderId(null)
          setBreadcrumbs([{ id: null, name: 'Główny katalog' }])
        }
      }
      await fetchFolders()
      await fetchProjectAttachments()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Folder został usunięty'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania folderu', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć folderu'
        })
      )
    }
  }

  // Funkcja nawigująca do folderu z breadcrumbs
  const navigateToFolder = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
    setBreadcrumbs(newBreadcrumbs)

    if (index === 0) {
      setCurrentFolderId(null)
    } else {
      setCurrentFolderId(newBreadcrumbs[index].id)
    }

    // Najpierw pobieramy foldery, a potem załączniki
    fetchFolders().then(() => {
      fetchProjectAttachments()
    })
  }

  // Funkcja obsługująca otwieranie folderu
  const handleOpenFolder = (folderId: string, folderName: string) => {
    setCurrentFolderId(folderId)
    setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }])
    // Najpierw pobieramy foldery, a potem załączniki
    fetchFolders().then(() => {
      fetchProjectAttachments()
    })
  }

  // Funkcja do formatowania rozmiaru pliku
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
  }

  // Funkcja do pobierania ikony dla typu pliku
  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return 'fa-file-image'
    if (fileType.includes('pdf')) return 'fa-file-pdf'
    if (fileType.includes('word') || fileType.includes('document')) return 'fa-file-word'
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fa-file-excel'
    if (fileType.includes('zip') || fileType.includes('compressed')) return 'fa-file-archive'
    return 'fa-file'
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        await fetchFolders()
        await fetchProjectAttachments()
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [fetchFolders, fetchProjectAttachments])

  return (
    <div className='row mb-30'>
      <div className='col-sm-12'>
        <div className='card p-30'>
          <div className='projectCard'>
            <div className='tw-mb-8 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-x-4 tw-gap-y-4 md:tw-gap-y-0'>
              <h4 className='tw-font-semibold'>Załączniki</h4>
              {!isReadOnly && (
                <div className='tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-gap-y-2 md:tw-gap-y-0'>
                  <button
                    className={`
                                        tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-transition-colors
                                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base
                                        ${showNewFolderForm
                                           ? 'tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500 tw-shadow-sm tw-text-white'
                                           : 'tw-bg-white hover:tw-bg-opacity-90 focus:tw-bg-white tw-shadow-lg focus:tw-shadow-lg tw-text-teal'
                                       }
                                      `}
                    onClick={() => setShowNewFolderForm(!showNewFolderForm)}
                  >
                    <i className='fas fa-folder-plus mr-2' />
                    {showNewFolderForm ? 'Anuluj' : 'Nowy folder'}
                  </button>

                  <input
                    type='file'
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleAttachmentUpload}
                  />

                  <button
                    className={`
                                          tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                          tw-text-white
                                          tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                        `}
                    onClick={handleAttachmentClick}
                    disabled={isUploadingAttachment}
                  >
                    {isUploadingAttachment
                        ? (
                            <>
                                <span
                                    className='tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent tw-mr-2'
                                    role='status'
                                    aria-hidden='true'
                                  />
                                                    Dodaję...
                              </>
                          )
                        : (
                            <>
                                <i className='fa fa-plus mr-2' />
                                Dodaj załącznik
                                                </>
                          )}
                  </button>
                </div>
              )}
            </div>

            {/* Formularz nowego folderu */}
            {showNewFolderForm && (
              <div className='mb-20 p-15 border rounded'>
                <div className='form-group mb-2'>
                  <label>Nazwa folderu</label>
                  <div className='d-flex'>
                    <input
                        type='text'
                        className='form-control mr-2'
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder='Wprowadź nazwę folderu'
                      />

                    <button
                        className={`
                                                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                                  tw-text-white
                                                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                                `}
                        onClick={handleCreateFolder}
                        disabled={!newFolderName.trim()}
                      >
                                          Utwórz
                      </button>
                  </div>
                </div>
              </div>
            )}

            {/* Breadcrumbs nawigacji folderów */}
            <nav aria-label='breadcrumb' className='mb-20'>
              <ol className='breadcrumb'>
                {breadcrumbs.map((folder, index) => (
                  <li
                    key={index}
                    className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                  >
                    {index === breadcrumbs.length - 1
                        ? (
                            folder.name
                          )
                        : (
                            <a href='#' onClick={(e) => { e.preventDefault(); navigateToFolder(index) }}>
                                {folder.name}
                              </a>
                          )}
                  </li>
                ))}
              </ol>
            </nav>

            {isLoading ? (
              <div className='text-center py-50'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='sr-only'>Ładowanie...</span>
                </div>
                <p className='mt-20'>Ładowanie załączników...</p>
              </div>
            ) : (
              <>
                {/* Lista folderów */}
                {folders.filter(folder =>
                  (currentFolderId === null && folder.parentId === null) ||
                                    folder.parentId === Number(currentFolderId)
                ).length > 0 && (
                    <div className='list-group mb-20'>
                        {folders
                            .filter(folder =>
                              (currentFolderId === null && folder.parentId === null) ||
                                                folder.parentId === Number(currentFolderId)
                            )
                            .map(folder => (
                                <div
                                    key={folder.id}
                                    className='list-group-item list-group-item-action d-flex justify-content-between align-items-center'
                                  >
                                    <div
                                        className='tw-w-full d-flex align-items-center cursor-pointer'
                                        onClick={() => handleOpenFolder(folder.id.toString(), folder.name)}
                                      >
                                        <img src='/assets/images/icons/Folder.png' alt='Folder' style={{ width: 50, height: 45, marginRight: 15, filter: 'grayscale(100%)' }} />
                                        <div>
                                          <h5 className='mb-1'>{folder.name}</h5>
                                          <small className='text-muted'>
                                              {folder._count.attachments} załączników • {folder.subfolders.length} podfolderów
                                            </small>
                                        </div>
                                      </div>
                                    <div>
                                        {!isReadOnly && (
                                          <button
                                              className='tw-group hover:tw-bg-red-500/30 tw-p-2 tw-rounded-md tw-font-semibold'
                                              onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id.toString()) }}
                                            >
                                              <span className='tw-text-red-500 group-hover:tw-text-red-700 focus:tw-text-red-500'>
                                                  <i className='fas fa-trash' />
                                                </span>
                                            </button>
                                        )}
                                      </div>
                                  </div>
                            ))}
                      </div>
                )}

                {/* Lista załączników */}
                {projectAttachments.length === 0 && folders.filter(folder =>
                  (currentFolderId === null && folder.parentId === null) ||
                                    folder.parentId === Number(currentFolderId)
                ).length === 0
                  ? (
                      <div className='alert alert-info'>
                          {currentFolderId === null
                              ? 'Ta lista nie ma jeszcze żadnych załączników ani folderów.'
                              : 'Ten folder jest pusty.'}
                        </div>
                    )
                  : (
                      <div className='list-group'>
                          {projectAttachments.map(attachment => (
                              <div key={attachment.id} className='list-group-item list-group-item-action d-flex justify-content-between align-items-center'>
                                  <div className='d-flex align-items-center'>
                                      <i className={`fas ${getFileIcon(attachment.type)} fa-3x mr-4`} />
                                      <div>
                                          <h5 className='mb-1'>{attachment.name}</h5>
                                          <small className='text-muted'>
                                            {formatFileSize(attachment.size)} • Dodano: {new Date(attachment.createdAt).toLocaleString()}
                                          </small>
                                        </div>
                                    </div>
                                  <div className='dropdown tw-flex tw-items-center tw-gap-2'>
                                      <button
                                          className='tw-group hover:tw-bg-blue-500/30 tw-p-2 tw-rounded-md tw-font-semibold'
                                          onClick={() => window.open(attachment.presignedUrl, '_blank')}
                                        >
                                          <span className='tw-text-blue-500 group-hover:tw-text-blue-700 focus:tw-text-blue-500'>
                                            <i className='fas fa-download mr-1' />
                                            Pobierz
                                                        </span>
                                        </button>

                                      {!isReadOnly && (
                                          <button
                                            className='tw-group hover:tw-bg-red-500/30 tw-p-2 tw-rounded-md tw-font-semibold'
                                            onClick={async () => await handleDeleteAttachment(attachment.id)}
                                          >
                                            <span className='tw-text-red-500 group-hover:tw-text-red-700 focus:tw-text-red-500'>
                                                <i className='fas fa-trash' />
                                              </span>
                                          </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
